import { useEffect, useState } from "react";
import type { CrewMember } from "@/mocks/admin/crew";
import { supabase } from "@/lib/supabase";
import { buildMoverUrl } from "@/lib/moverLink";

type Props = {
  member: CrewMember;
};

type SendState = "idle" | "sending" | "success" | "error";

type InviteRow = {
  id: number;
  channel: "email" | "sms";
  recipient: string;
  status: "sent" | "failed";
  created_at: string;
  error_message: string | null;
};

type ProviderStatus = {
  email: { configured: boolean; provider: string; customSender: boolean; missing: string[] };
  sms: { configured: boolean; provider: string; missing: string[] };
};

export default function MoverLinkCard({ member }: Props) {
  const [copied, setCopied] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const [emailState, setEmailState] = useState<SendState>("idle");
  const [smsState, setSmsState] = useState<SendState>("idle");
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);
  const [history, setHistory] = useState<InviteRow[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [providers, setProviders] = useState<ProviderStatus | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [recheckState, setRecheckState] = useState<"idle" | "checking">("idle");
  const [testEmail, setTestEmail] = useState("");
  const [testState, setTestState] = useState<SendState>("idle");
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    message: string;
    from?: string;
    customSender?: boolean;
  } | null>(null);
  const [testPhone, setTestPhone] = useState("");
  const [smsTestState, setSmsTestState] = useState<SendState>("idle");
  const [smsTestResult, setSmsTestResult] = useState<{
    ok: boolean;
    message: string;
    from?: string;
    to?: string;
    code?: number;
    hint?: string;
    moreInfo?: string;
  } | null>(null);
  const [diagState, setDiagState] = useState<"idle" | "checking">("idle");
  const [diagResult, setDiagResult] = useState<{
    checks: Record<string, string>;
    relatedKeysSeen: string[];
    note?: string;
  } | null>(null);

  const fullUrl = buildMoverUrl(member.accessToken);

  // Check which messaging providers are connected
  const refreshProviders = async (announce = false) => {
    if (announce) setRecheckState("checking");
    try {
      const { data } = await supabase.functions.invoke(
        "mover-link-providers-status",
        { body: {} },
      );
      if (data) {
        setProviders(data as ProviderStatus);
        if (announce) {
          const next = data as ProviderStatus;
          if (next.sms.configured && next.email.configured) {
            showToast("ok", "Twilio and Resend are both connected.");
          } else if (next.sms.configured) {
            showToast("ok", "Twilio connected. Resend still needs setup.");
          } else if (next.email.configured) {
            showToast("ok", "Resend connected. Twilio still needs setup.");
          } else {
            showToast(
              "err",
              `Still missing: ${[...next.sms.missing, ...next.email.missing].join(", ")}`,
            );
          }
        }
      }
    } catch (e) {
      if (announce) showToast("err", (e as Error).message || "Could not reach Supabase");
    } finally {
      if (announce) setTimeout(() => setRecheckState("idle"), 600);
    }
  };

  useEffect(() => {
    let cancelled = false;
    supabase.functions
      .invoke("mover-link-providers-status", { body: {} })
      .then(({ data }) => {
        if (!cancelled && data) setProviders(data as ProviderStatus);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Load recent send history for this crew member
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingHistory(true);
      const { data } = await supabase
        .from("mover_invites")
        .select("id, channel, recipient, status, created_at, error_message")
        .eq("crew_member_id", member.id)
        .order("created_at", { ascending: false })
        .limit(5);
      if (!cancelled) {
        setHistory((data as InviteRow[]) || []);
        setLoadingHistory(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [member.id]);

  if (!member.accessToken) {
    return (
      <div className="bg-white border border-ink-100 rounded-lg p-5">
        <h3 className="font-display text-base font-bold text-ink-900 mb-2">Mover App Link</h3>
        <p className="text-xs text-ink-500">
          No private link has been generated yet for this crew member.
        </p>
      </div>
    );
  }

  const masked = `${fullUrl.slice(0, 32)}…${member.accessToken.slice(-6)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
    } catch {
      // ignore
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const showToast = (kind: "ok" | "err", msg: string) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const logInvite = async (
    channel: "email" | "sms",
    recipient: string,
    status: "sent" | "failed",
    providerId?: string,
    errorMessage?: string,
  ) => {
    const { data: sess } = await supabase.auth.getSession();
    await supabase.from("mover_invites").insert({
      crew_member_id: member.id,
      crew_member_name: member.name,
      channel,
      recipient,
      link: fullUrl,
      status,
      provider_id: providerId ?? null,
      error_message: errorMessage ?? null,
      sent_by: sess.session?.user.id ?? null,
    });

    // Reload history (best effort)
    const { data } = await supabase
      .from("mover_invites")
      .select("id, channel, recipient, status, created_at, error_message")
      .eq("crew_member_id", member.id)
      .order("created_at", { ascending: false })
      .limit(5);
    setHistory((data as InviteRow[]) || []);
  };

  const sendEmail = async () => {
    if (!member.email) {
      showToast("err", "This crew member has no email on file.");
      return;
    }
    setEmailState("sending");
    try {
      const { data, error } = await supabase.functions.invoke("send-mover-link-email", {
        body: { to: member.email, name: member.name, link: fullUrl },
      });
      if (error || (data && data.error)) {
        const msg = (data && data.error) || error?.message || "Failed to send email";
        await logInvite("email", member.email, "failed", undefined, msg);
        setEmailState("error");
        showToast("err", msg);
      } else {
        await logInvite("email", member.email, "sent", data?.id);
        setEmailState("success");
        showToast("ok", `Email sent to ${member.email}`);
      }
    } catch (e) {
      const msg = (e as Error).message || "Network error";
      await logInvite("email", member.email, "failed", undefined, msg);
      setEmailState("error");
      showToast("err", msg);
    }
    setTimeout(() => setEmailState("idle"), 2200);
  };

  const sendSms = async () => {
    if (!member.phone) {
      showToast("err", "This crew member has no phone number on file.");
      return;
    }
    setSmsState("sending");
    try {
      const { data, error } = await supabase.functions.invoke("send-mover-link-sms", {
        body: { to: member.phone, name: member.name, link: fullUrl },
      });
      if (error || (data && data.error)) {
        const msg = (data && data.error) || error?.message || "Failed to send SMS";
        await logInvite("sms", member.phone, "failed", undefined, msg);
        setSmsState("error");
        showToast("err", msg);
      } else {
        await logInvite("sms", member.phone, "sent", data?.sid);
        setSmsState("success");
        showToast("ok", `Text sent to ${member.phone}`);
      }
    } catch (e) {
      const msg = (e as Error).message || "Network error";
      await logInvite("sms", member.phone, "failed", undefined, msg);
      setSmsState("error");
      showToast("err", msg);
    }
    setTimeout(() => setSmsState("idle"), 2200);
  };

  const btnLabel = (state: SendState, idle: string) => {
    if (state === "sending") return "Sending…";
    if (state === "success") return "Sent!";
    if (state === "error") return "Failed";
    return idle;
  };

  const btnIcon = (state: SendState, idle: string) => {
    if (state === "sending") return "ri-loader-4-line animate-spin";
    if (state === "success") return "ri-check-line";
    if (state === "error") return "ri-error-warning-line";
    return idle;
  };

  const btnTone = (state: SendState) => {
    if (state === "success") return "bg-emerald-500 text-white";
    if (state === "error") return "bg-rose-100 text-rose-700";
    if (state === "sending") return "bg-ink-100 text-ink-500";
    return "bg-cream-100 hover:bg-ink-100 text-ink-700";
  };

  const timeAgo = (iso: string) => {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.round(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    return `${d}d ago`;
  };

  return (
    <div className="bg-white border border-ink-100 rounded-lg p-5 relative">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-display text-base font-bold text-ink-900">Private Mover App</h3>
          <p className="text-[11px] text-ink-500 mt-0.5">
            {member.name.split(" ")[0]} only sees their own jobs, schedule, and pay
          </p>
        </div>
        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full whitespace-nowrap">
          <i className="ri-shield-check-line"></i>
          Token secured
        </span>
      </div>

      <div className="bg-cream-50 border border-ink-100 rounded-md p-3 mb-3">
        <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold mb-1.5">
          Personal link
        </div>
        <div className="flex items-center gap-2">
          <code
            onClick={() => setShowFull((v) => !v)}
            className="flex-1 text-[11px] font-mono text-ink-700 break-all cursor-pointer hover:text-ink-900"
            title="Click to toggle full URL"
          >
            {showFull ? fullUrl : masked}
          </code>
          <button
            onClick={copy}
            className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1.5 rounded-md cursor-pointer whitespace-nowrap transition-colors ${
              copied
                ? "bg-emerald-500 text-white"
                : "bg-ink-900 hover:bg-ink-800 text-white"
            }`}
          >
            <i className={copied ? "ri-check-line" : "ri-file-copy-line"}></i>
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <button
          onClick={sendSms}
          disabled={smsState === "sending"}
          className={`inline-flex items-center justify-center gap-1.5 py-2 rounded-md text-[11px] font-bold cursor-pointer whitespace-nowrap disabled:cursor-not-allowed transition-colors ${btnTone(
            smsState,
          )}`}
        >
          <i className={btnIcon(smsState, "ri-message-2-line")}></i>
          {btnLabel(smsState, "Text")}
        </button>
        <button
          onClick={sendEmail}
          disabled={emailState === "sending"}
          className={`inline-flex items-center justify-center gap-1.5 py-2 rounded-md text-[11px] font-bold cursor-pointer whitespace-nowrap disabled:cursor-not-allowed transition-colors ${btnTone(
            emailState,
          )}`}
        >
          <i className={btnIcon(emailState, "ri-mail-line")}></i>
          {btnLabel(emailState, "Email")}
        </button>
        <a
          href={fullUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-1.5 py-2 rounded-md bg-cream-100 hover:bg-ink-100 text-ink-700 text-[11px] font-bold cursor-pointer whitespace-nowrap"
        >
          <i className="ri-external-link-line"></i>
          Preview
        </a>
      </div>

      {/* Send history */}
      <div className="mt-4 pt-3 border-t border-ink-100">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
            Recent sends
          </div>
          {history.length > 0 && (
            <span className="text-[10px] text-ink-400">{history.length} most recent</span>
          )}
        </div>
        {loadingHistory ? (
          <div className="text-[11px] text-ink-400 py-2">Loading…</div>
        ) : history.length === 0 ? (
          <div className="text-[11px] text-ink-400 py-2">
            No invites sent yet. Tap <span className="font-semibold">Text</span> or{" "}
            <span className="font-semibold">Email</span> above to send {member.name.split(" ")[0]}{" "}
            their link.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {history.map((row) => (
              <li
                key={row.id}
                className="flex items-center gap-2 text-[11px] text-ink-600"
                title={row.error_message || ""}
              >
                <span
                  className={`w-5 h-5 flex items-center justify-center rounded-full shrink-0 ${
                    row.status === "sent"
                      ? "bg-emerald-50 text-emerald-600"
                      : "bg-rose-50 text-rose-600"
                  }`}
                >
                  <i
                    className={
                      row.channel === "email" ? "ri-mail-line text-xs" : "ri-message-2-line text-xs"
                    }
                  ></i>
                </span>
                <span className="font-mono text-ink-700 truncate flex-1">{row.recipient}</span>
                <span
                  className={`font-semibold ${
                    row.status === "sent" ? "text-emerald-600" : "text-rose-600"
                  }`}
                >
                  {row.status === "sent" ? "delivered" : "failed"}
                </span>
                <span className="text-ink-400 tabular-nums">{timeAgo(row.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Provider setup status */}
      {providers && (
        <div className="mt-4 pt-3 border-t border-ink-100">
          <div className="w-full flex items-center justify-between text-[11px]">
            <button
              onClick={() => setSetupOpen((v) => !v)}
              className="flex items-center gap-2 cursor-pointer group flex-1 min-w-0"
            >
              <span className="font-bold uppercase tracking-wider text-ink-400 group-hover:text-ink-600">
                Delivery setup
              </span>
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  providers.sms.configured
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-amber-50 text-amber-700"
                }`}
              >
                <i
                  className={
                    providers.sms.configured ? "ri-check-line" : "ri-error-warning-line"
                  }
                ></i>
                SMS
              </span>
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  providers.email.configured
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-amber-50 text-amber-700"
                }`}
              >
                <i
                  className={
                    providers.email.configured ? "ri-check-line" : "ri-error-warning-line"
                  }
                ></i>
                Email
              </span>
            </button>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => refreshProviders(true)}
                disabled={recheckState === "checking"}
                title="Re-check secrets in Supabase"
                className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-md bg-cream-100 hover:bg-ink-100 text-ink-700 cursor-pointer disabled:cursor-not-allowed whitespace-nowrap"
              >
                <i
                  className={
                    recheckState === "checking"
                      ? "ri-loader-4-line animate-spin"
                      : "ri-refresh-line"
                  }
                ></i>
                {recheckState === "checking" ? "Checking…" : "Recheck"}
              </button>
              <button
                onClick={() => setSetupOpen((v) => !v)}
                className="w-6 h-6 flex items-center justify-center text-ink-400 hover:text-ink-600 cursor-pointer"
              >
                <i
                  className={setupOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}
                ></i>
              </button>
            </div>
          </div>

          {setupOpen && (
            <div className="mt-3 space-y-3">
              {/* Quick test sender (visible when Resend key is present) */}
              {providers.email.configured && (
                <div className="bg-white border border-ink-200 rounded-md p-3">
                  <div className="text-[11px] font-bold text-ink-900 mb-1.5 flex items-center gap-1.5">
                    <i className="ri-flask-line text-ink-500"></i>
                    Send a test email
                  </div>
                  <div className="text-[10px] text-ink-500 mb-2 leading-relaxed">
                    {providers.email.customSender ? (
                      <>Send a quick test to any address to verify delivery.</>
                    ) : (
                      <>
                        You haven’t set <code className="font-mono">RESEND_FROM</code> yet, so
                        Resend’s test sender will only deliver to the email you signed up to
                        Resend with.
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="you@yourdomain.com"
                      className="flex-1 text-[11px] px-2.5 py-1.5 rounded-md border border-ink-200 focus:border-ink-400 outline-none bg-white"
                    />
                    <button
                      onClick={async () => {
                        if (!testEmail || testState === "sending") return;
                        setTestState("sending");
                        setTestResult(null);
                        try {
                          const { data, error } = await supabase.functions.invoke(
                            "test-resend",
                            { body: { to: testEmail } },
                          );
                          if (error) {
                            setTestState("error");
                            setTestResult({
                              ok: false,
                              message: error.message || "Network error",
                            });
                          } else if (data?.ok) {
                            setTestState("success");
                            setTestResult({
                              ok: true,
                              message: `Sent! Check ${testEmail}’s inbox (and spam).`,
                              from: data.from,
                              customSender: data.usedCustomSender,
                            });
                            showToast("ok", `Test sent to ${testEmail}`);
                          } else {
                            setTestState("error");
                            setTestResult({
                              ok: false,
                              message:
                                data?.error || "Resend rejected the request.",
                            });
                          }
                        } catch (e) {
                          setTestState("error");
                          setTestResult({
                            ok: false,
                            message: (e as Error).message || "Unknown error",
                          });
                        }
                        setTimeout(() => setTestState("idle"), 2500);
                      }}
                      disabled={!testEmail || testState === "sending"}
                      className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1.5 rounded-md cursor-pointer whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50 transition-colors ${
                        testState === "success"
                          ? "bg-emerald-500 text-white"
                          : testState === "error"
                          ? "bg-rose-100 text-rose-700"
                          : "bg-ink-900 hover:bg-ink-800 text-white"
                      }`}
                    >
                      <i
                        className={
                          testState === "sending"
                            ? "ri-loader-4-line animate-spin"
                            : testState === "success"
                            ? "ri-check-line"
                            : testState === "error"
                            ? "ri-error-warning-line"
                            : "ri-send-plane-line"
                        }
                      ></i>
                      {testState === "sending"
                        ? "Sending…"
                        : testState === "success"
                        ? "Sent"
                        : testState === "error"
                        ? "Failed"
                        : "Test"}
                    </button>
                  </div>
                  {testResult && (
                    <div
                      className={`mt-2 text-[11px] leading-relaxed p-2 rounded-md ${
                        testResult.ok
                          ? "bg-emerald-50 text-emerald-800"
                          : "bg-rose-50 text-rose-800"
                      }`}
                    >
                      <div className="font-semibold flex items-start gap-1">
                        <i
                          className={`mt-0.5 ${
                            testResult.ok
                              ? "ri-check-double-line"
                              : "ri-error-warning-line"
                          }`}
                        ></i>
                        <span>{testResult.message}</span>
                      </div>
                      {testResult.ok && testResult.from && (
                        <div className="mt-1 text-[10px] opacity-80">
                          From:{" "}
                          <code className="font-mono">{testResult.from}</code>
                          {!testResult.customSender && (
                            <>
                              {" "}· add{" "}
                              <code className="font-mono">RESEND_FROM</code> for production
                              sender.
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Quick test sender for SMS (visible when Twilio is configured) */}
              {providers.sms.configured && (
                <div className="bg-white border border-ink-200 rounded-md p-3">
                  <div className="text-[11px] font-bold text-ink-900 mb-1.5 flex items-center gap-1.5">
                    <i className="ri-flask-line text-ink-500"></i>
                    Send a test SMS
                  </div>
                  <div className="text-[10px] text-ink-500 mb-2 leading-relaxed">
                    Send a quick text to any phone to verify Twilio delivery. US numbers can be entered without the country code.
                  </div>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="tel"
                      value={testPhone}
                      onChange={(e) => setTestPhone(e.target.value)}
                      placeholder="+1 512 555 0142"
                      className="flex-1 text-[11px] px-2.5 py-1.5 rounded-md border border-ink-200 focus:border-ink-400 outline-none bg-white"
                    />
                    <button
                      onClick={async () => {
                        if (!testPhone || smsTestState === "sending") return;
                        setSmsTestState("sending");
                        setSmsTestResult(null);
                        try {
                          const { data, error } = await supabase.functions.invoke(
                            "test-twilio",
                            { body: { to: testPhone } },
                          );
                          // The edge function now always returns 200 with an `ok` flag,
                          // so `data` carries the real result even when Twilio rejected.
                          const payload = data || {};
                          if (payload.ok) {
                            setSmsTestState("success");
                            setSmsTestResult({
                              ok: true,
                              message: `Sent! Twilio accepted the message for ${payload.to}.`,
                              from: payload.from,
                              to: payload.to,
                            });
                            showToast("ok", `Test SMS sent to ${payload.to}`);
                          } else if (payload.error) {
                            setSmsTestState("error");
                            setSmsTestResult({
                              ok: false,
                              message: payload.error,
                              code: payload.code,
                              hint: payload.hint,
                              moreInfo: payload.moreInfo,
                            });
                          } else if (error) {
                            setSmsTestState("error");
                            setSmsTestResult({
                              ok: false,
                              message: error.message || "Network error",
                            });
                          } else {
                            setSmsTestState("error");
                            setSmsTestResult({
                              ok: false,
                              message: "Twilio rejected the request.",
                            });
                          }
                        } catch (e) {
                          setSmsTestState("error");
                          setSmsTestResult({
                            ok: false,
                            message: (e as Error).message || "Unknown error",
                          });
                        }
                        setTimeout(() => setSmsTestState("idle"), 2500);
                      }}
                      disabled={!testPhone || smsTestState === "sending"}
                      className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1.5 rounded-md cursor-pointer whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50 transition-colors ${
                        smsTestState === "success"
                          ? "bg-emerald-500 text-white"
                          : smsTestState === "error"
                          ? "bg-rose-100 text-rose-700"
                          : "bg-ink-900 hover:bg-ink-800 text-white"
                      }`}
                    >
                      <i
                        className={
                          smsTestState === "sending"
                            ? "ri-loader-4-line animate-spin"
                            : smsTestState === "success"
                            ? "ri-check-line"
                            : smsTestState === "error"
                            ? "ri-error-warning-line"
                            : "ri-send-plane-line"
                        }
                      ></i>
                      {smsTestState === "sending"
                        ? "Sending…"
                        : smsTestState === "success"
                        ? "Sent"
                        : smsTestState === "error"
                        ? "Failed"
                        : "Test"}
                    </button>
                  </div>
                  {smsTestResult && (
                    <div
                      className={`mt-2 text-[11px] leading-relaxed p-2 rounded-md ${
                        smsTestResult.ok
                          ? "bg-emerald-50 text-emerald-800"
                          : "bg-rose-50 text-rose-800"
                      }`}
                    >
                      <div className="font-semibold flex items-start gap-1">
                        <i
                          className={`mt-0.5 ${
                            smsTestResult.ok
                              ? "ri-check-double-line"
                              : "ri-error-warning-line"
                          }`}
                        ></i>
                        <span>
                          {smsTestResult.code
                            ? `Twilio error ${smsTestResult.code}: ${smsTestResult.message}`
                            : smsTestResult.message}
                        </span>
                      </div>
                      {smsTestResult.hint && (
                        <div className="mt-1.5 pl-4 text-[10px] opacity-90">
                          <span className="font-semibold">Fix:</span> {smsTestResult.hint}
                        </div>
                      )}
                      {smsTestResult.moreInfo && (
                        <div className="mt-1 pl-4 text-[10px] opacity-70">
                          <a
                            href={smsTestResult.moreInfo}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:opacity-100"
                          >
                            Twilio docs for this error →
                          </a>
                        </div>
                      )}
                      {smsTestResult.ok && smsTestResult.from && (
                        <div className="mt-1 text-[10px] opacity-80">
                          From: <code className="font-mono">{smsTestResult.from}</code>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Email setup */}
              <div
                className={`rounded-md border p-3 ${
                  providers.email.configured
                    ? "bg-emerald-50/50 border-emerald-200"
                    : "bg-amber-50/50 border-amber-200"
                }`}
              >
                <div className="flex items-start gap-2 mb-1.5">
                  <span
                    className={`w-5 h-5 flex items-center justify-center rounded-full text-[10px] shrink-0 mt-0.5 ${
                      providers.email.configured
                        ? "bg-emerald-500 text-white"
                        : "bg-amber-500 text-white"
                    }`}
                  >
                    <i
                      className={
                        providers.email.configured
                          ? "ri-check-line"
                          : "ri-error-warning-line"
                      }
                    ></i>
                  </span>
                  <div className="flex-1">
                    <div className="text-xs font-bold text-ink-900 flex items-center gap-1.5">
                      Email · Resend
                      {providers.email.configured && !providers.email.customSender && (
                        <span className="text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">
                          test sender
                        </span>
                      )}
                    </div>
                    {providers.email.configured ? (
                      <div className="text-[11px] text-ink-600 mt-0.5 leading-relaxed">
                        {providers.email.customSender ? (
                          <>Sending from your verified domain. ✓</>
                        ) : (
                          <>
                            Working with Resend's test sender. Real crew can only receive
                            if their email matches your Resend account email. Add{" "}
                            <code className="font-mono text-[10px] bg-white border border-ink-100 px-1 py-0.5 rounded">
                              RESEND_FROM
                            </code>{" "}
                            to deliver to anyone.
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="text-[11px] text-ink-700 mt-1 leading-relaxed">
                        <ol className="list-decimal ml-4 space-y-1">
                          <li>
                            Create a free account at{" "}
                            <a
                              href="https://resend.com/signup"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-brand-700 font-semibold hover:underline"
                            >
                              resend.com
                            </a>
                            .
                          </li>
                          <li>
                            Go to <span className="font-semibold">API Keys</span> →{" "}
                            <span className="font-semibold">Create API Key</span> → copy the
                            value (starts with{" "}
                            <code className="font-mono text-[10px] bg-white border border-ink-100 px-1 rounded">
                              re_
                            </code>
                            ).
                          </li>
                          <li>
                            In Supabase → <span className="font-semibold">Edge Functions</span>{" "}
                            → <span className="font-semibold">Secrets</span>, add:
                            <ul className="mt-1 space-y-0.5">
                              <li>
                                <code className="font-mono text-[10px] bg-white border border-ink-100 px-1 rounded">
                                  RESEND_API_KEY
                                </code>{" "}
                                = your key
                              </li>
                              <li>
                                <code className="font-mono text-[10px] bg-white border border-ink-100 px-1 rounded">
                                  RESEND_FROM
                                </code>{" "}
                                = e.g.{" "}
                                <code className="font-mono text-[10px] bg-white border border-ink-100 px-1 rounded">
                                  Dispatch &lt;dispatch@yourdomain.com&gt;
                                </code>{" "}
                                (optional, recommended)
                              </li>
                            </ul>
                          </li>
                        </ol>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* SMS setup */}
              <div
                className={`rounded-md border p-3 ${
                  providers.sms.configured
                    ? "bg-emerald-50/50 border-emerald-200"
                    : "bg-amber-50/50 border-amber-200"
                }`}
              >
                <div className="flex items-start gap-2">
                  <span
                    className={`w-5 h-5 flex items-center justify-center rounded-full text-[10px] shrink-0 mt-0.5 ${
                      providers.sms.configured
                        ? "bg-emerald-500 text-white"
                        : "bg-amber-500 text-white"
                    }`}
                  >
                    <i
                      className={
                        providers.sms.configured
                          ? "ri-check-line"
                          : "ri-error-warning-line"
                      }
                    ></i>
                  </span>
                  <div className="flex-1">
                    <div className="text-xs font-bold text-ink-900">SMS · Twilio</div>
                    {providers.sms.configured ? (
                      <div className="text-[11px] text-ink-600 mt-0.5">
                        Twilio is connected and ready to text. ✓
                      </div>
                    ) : (
                      <div className="text-[11px] text-ink-700 mt-1 leading-relaxed">
                        <ol className="list-decimal ml-4 space-y-1">
                          <li>
                            Sign up / log in at{" "}
                            <a
                              href="https://www.twilio.com/try-twilio"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-brand-700 font-semibold hover:underline"
                            >
                              twilio.com
                            </a>
                            . On the Console you'll see your{" "}
                            <span className="font-semibold">Account SID</span> and{" "}
                            <span className="font-semibold">Auth Token</span>.
                          </li>
                          <li>
                            Buy or use a Twilio phone number (must be in E.164 format like{" "}
                            <code className="font-mono text-[10px] bg-white border border-ink-100 px-1 rounded">
                              +15125550100
                            </code>
                            ).
                          </li>
                          <li>
                            In Supabase → <span className="font-semibold">Edge Functions</span>{" "}
                            → <span className="font-semibold">Secrets</span>, add:
                            <ul className="mt-1 space-y-0.5">
                              <li>
                                <code className="font-mono text-[10px] bg-white border border-ink-100 px-1 rounded">
                                  TWILIO_ACCOUNT_SID
                                </code>{" "}
                                = starts with{" "}
                                <code className="font-mono text-[10px] bg-white border border-ink-100 px-1 rounded">
                                  AC…
                                </code>
                              </li>
                              <li>
                                <code className="font-mono text-[10px] bg-white border border-ink-100 px-1 rounded">
                                  TWILIO_AUTH_TOKEN
                                </code>{" "}
                                = your auth token
                              </li>
                              <li>
                                <code className="font-mono text-[10px] bg-white border border-ink-100 px-1 rounded">
                                  TWILIO_FROM
                                </code>{" "}
                                = your phone (E.164) or a Messaging Service SID (
                                <code className="font-mono text-[10px] bg-white border border-ink-100 px-1 rounded">
                                  MG…
                                </code>
                                )
                              </li>
                            </ul>
                          </li>
                          {providers.sms.missing.length > 0 && (
                            <li className="text-amber-800">
                              Still missing:{" "}
                              {providers.sms.missing.map((m, i) => (
                                <span key={m}>
                                  <code className="font-mono text-[10px] bg-white border border-ink-100 px-1 rounded">
                                    {m}
                                  </code>
                                  {i < providers.sms.missing.length - 1 ? ", " : ""}
                                </span>
                              ))}
                            </li>
                          )}
                        </ol>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <a
                href="https://supabase.com/dashboard/project/_/settings/functions"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-md bg-ink-900 hover:bg-ink-800 text-white text-[11px] font-bold cursor-pointer whitespace-nowrap"
              >
                <i className="ri-external-link-line"></i>
                Open Supabase Secrets
              </a>
              <div className="text-[10px] text-ink-400 text-center">
                After saving secrets, refresh this page to see the green check.
              </div>

              {/* Diagnostic — proves whether secrets are actually loaded by the edge runtime */}
              <div className="bg-white border border-ink-200 rounded-md p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[11px] font-bold text-ink-900 flex items-center gap-1.5">
                    <i className="ri-stethoscope-line text-ink-500"></i>
                    Secret diagnostic
                  </div>
                  <button
                    onClick={async () => {
                      setDiagState("checking");
                      setDiagResult(null);
                      try {
                        const { data, error } = await supabase.functions.invoke(
                          "env-diag",
                          { body: {} },
                        );
                        if (error) {
                          showToast("err", error.message || "Could not reach env-diag");
                        } else if (data) {
                          setDiagResult(data);
                          await refreshProviders(false);
                        }
                      } catch (e) {
                        showToast("err", (e as Error).message || "Network error");
                      } finally {
                        setDiagState("idle");
                      }
                    }}
                    disabled={diagState === "checking"}
                    className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-md bg-ink-900 hover:bg-ink-800 text-white cursor-pointer disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    <i
                      className={
                        diagState === "checking"
                          ? "ri-loader-4-line animate-spin"
                          : "ri-search-line"
                      }
                    ></i>
                    {diagState === "checking" ? "Checking…" : "Run diagnostic"}
                  </button>
                </div>
                <div className="text-[10px] text-ink-500 leading-relaxed mb-2">
                  Pings the edge runtime to confirm whether your Twilio/Resend secrets are
                  actually being loaded. Values are never exposed — only names and lengths.
                </div>
                {diagResult && (
                  <div className="space-y-1.5">
                    <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
                      Expected secrets
                    </div>
                    <ul className="space-y-1">
                      {Object.entries(diagResult.checks).map(([key, val]) => {
                        const missing = val === "<missing>";
                        return (
                          <li
                            key={key}
                            className={`flex items-center justify-between gap-2 text-[11px] px-2 py-1 rounded ${
                              missing
                                ? "bg-rose-50 text-rose-800"
                                : "bg-emerald-50 text-emerald-800"
                            }`}
                          >
                            <code className="font-mono">{key}</code>
                            <span className="text-[10px] font-semibold">
                              <i
                                className={`mr-1 ${
                                  missing
                                    ? "ri-close-circle-line"
                                    : "ri-checkbox-circle-line"
                                }`}
                              ></i>
                              {missing ? "not seen" : val}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                    {diagResult.relatedKeysSeen.length > 0 && (
                      <>
                        <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold mt-2">
                          All Twilio/Resend-prefixed names found
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {diagResult.relatedKeysSeen.map((k) => (
                            <code
                              key={k}
                              className="font-mono text-[10px] bg-cream-100 border border-ink-100 px-1.5 py-0.5 rounded"
                            >
                              {k}
                            </code>
                          ))}
                        </div>
                      </>
                    )}
                    {diagResult.relatedKeysSeen.length === 0 && (
                      <div className="text-[10px] text-ink-500 mt-2 leading-relaxed">
                        Nothing matching <code className="font-mono">TWILIO_*</code> or{" "}
                        <code className="font-mono">RESEND_*</code> was found in this edge
                        function's environment. That almost always means the secrets were
                        saved in a different Supabase project, or weren't saved at all.
                        Compare the project ref in your dashboard URL with the one this app
                        is connected to.
                      </div>
                    )}
                    {diagResult.note && (
                      <div className="text-[10px] text-ink-500 italic mt-2 leading-relaxed">
                        {diagResult.note}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-ink-100 text-[11px] text-ink-500 leading-relaxed">
        <span className="font-semibold text-ink-700">Tip:</span> Anyone with this URL can access{" "}
        {member.name.split(" ")[0]}'s mover app. Treat it like a password.
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 max-w-sm px-4 py-3 rounded-lg text-sm font-semibold flex items-start gap-2 animate-in slide-in-from-bottom-2 ${
            toast.kind === "ok"
              ? "bg-emerald-600 text-white"
              : "bg-rose-600 text-white"
          }`}
        >
          <i
            className={`mt-0.5 ${
              toast.kind === "ok" ? "ri-check-double-line" : "ri-error-warning-line"
            }`}
          ></i>
          <span className="flex-1">{toast.msg}</span>
          <button
            onClick={() => setToast(null)}
            className="opacity-70 hover:opacity-100 cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}
    </div>
  );
}